/**
 * ReseauNotificationBell — Lot 1B.2
 * Cloche dédiée aux notifications du réseau social Homly (new_follower,
 * profile_shared, completeness_reminder, etc.). Distincte de la cloche
 * notifications legacy (rappels mandats, alertes commerciales).
 *
 * - Dropdown popover compact avec les 8 dernières notifications.
 * - Badge "unread_count" mis à jour par polling 30 s.
 * - Clic sur une notification : marque lue + navigation vers le lien.
 * - Bouton "Tout marquer lu" + "Voir tout".
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getToken } from '../../lib/api';
import { Button } from '../ui/button';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent
} from '../ui/dropdown-menu';
import { Users, Heart, Share2, CheckCircle, Award, ShieldAlert, MessageCircle, Sparkles, Check } from 'lucide-react';

const POLL_MS = 30000;

const ICON_BY_TYPE = {
    new_follower: Users,
    profile_shared: Share2,
    completeness_reminder: ShieldAlert,
    verification_available: Award,
    new_interaction: Heart,
    new_contact_request: MessageCircle,
    new_review: Award,
    agency_followed: Users,
    new_comment: MessageCircle,
    new_reaction: Heart,
};

function timeAgo(iso) {
    if (!iso) return '';
    const now = Date.now();
    const t = new Date(iso).getTime();
    const diff = Math.max(0, now - t);
    const min = Math.floor(diff / 60000);
    if (min < 1) return "à l'instant";
    if (min < 60) return `il y a ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `il y a ${h} h`;
    const d = Math.floor(h / 24);
    if (d < 7) return `il y a ${d} j`;
    return new Date(iso).toLocaleDateString('fr-FR');
}

export default function ReseauNotificationBell({ basePath = '' }) {
    const navigate = useNavigate();
    const [unreadCount, setUnreadCount] = useState(0);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const isAuthed = !!getToken();

    const refreshCount = useCallback(async () => {
        if (!isAuthed) return;
        try {
            const { data } = await api.get('/reseau/notifications/unread-count');
            setUnreadCount(data.unread_count || 0);
        } catch (e) { /* silent */ }
    }, [isAuthed]);

    const loadList = useCallback(async () => {
        if (!isAuthed) return;
        setLoading(true);
        try {
            const { data } = await api.get('/reseau/notifications?limit=8');
            setItems(data.items || []);
            setUnreadCount(data.unread_count || 0);
        } catch (e) { /* silent */ } finally {
            setLoading(false);
        }
    }, [isAuthed]);

    useEffect(() => {
        if (!isAuthed) return;
        refreshCount();
        const intv = setInterval(refreshCount, POLL_MS);
        return () => clearInterval(intv);
    }, [isAuthed, refreshCount]);

    useEffect(() => {
        if (open) loadList();
    }, [open, loadList]);

    const handleItemClick = async (n) => {
        // Marque comme lue (best-effort)
        if (!n.read_at) {
            try {
                await api.post(`/reseau/notifications/${n.id}/read`);
                setUnreadCount((c) => Math.max(0, c - 1));
            } catch (e) { /* silent */ }
        }
        setOpen(false);
        if (n.link) navigate(n.link);
    };

    const handleMarkAllRead = async () => {
        try {
            await api.post('/reseau/notifications/read-all');
            setUnreadCount(0);
            setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
        } catch (e) { /* silent */ }
    };

    if (!isAuthed) return null;

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="relative text-muted-foreground hover:text-white hover:bg-card"
                    data-testid="reseau-notifications-button"
                    aria-label={`Notifications réseau · ${unreadCount} non lue${unreadCount > 1 ? 's' : ''}`}
                >
                    <Sparkles className="w-5 h-5" />
                    {unreadCount > 0 && (
                        <span
                            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-r from-purple-500 to-orange-500 text-white text-[10px] font-bold flex items-center justify-center"
                            data-testid="reseau-notif-badge"
                        >
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                className="w-96 max-h-[480px] overflow-y-auto bg-zinc-900 border-white/10 text-white p-0"
                data-testid="reseau-notif-dropdown"
            >
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                    <div>
                        <div className="font-semibold text-sm">Réseau Homly</div>
                        <div className="text-xs text-white/40">
                            {unreadCount > 0 ? `${unreadCount} non lue${unreadCount > 1 ? 's' : ''}` : 'Tout est à jour'}
                        </div>
                    </div>
                    {unreadCount > 0 && (
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleMarkAllRead}
                            className="text-xs h-auto py-1 px-2 text-white/60 hover:text-white"
                            data-testid="mark-all-read-btn"
                        >
                            <Check className="w-3 h-3 mr-1" /> Tout lu
                        </Button>
                    )}
                </div>

                {loading && items.length === 0 && (
                    <div className="p-6 text-center text-sm text-white/40" data-testid="notif-loading">
                        Chargement…
                    </div>
                )}

                {!loading && items.length === 0 && (
                    <div className="p-6 text-center text-sm text-white/40" data-testid="notif-empty">
                        Aucune notification pour le moment.
                    </div>
                )}

                {items.map((n) => {
                    const Icon = ICON_BY_TYPE[n.type] || Sparkles;
                    const isUnread = !n.read_at;
                    return (
                        <button
                            key={n.id}
                            type="button"
                            onClick={() => handleItemClick(n)}
                            data-testid={`notif-item-${n.id}`}
                            className={`w-full flex gap-3 px-4 py-3 text-left border-b border-white/5 hover:bg-white/5 transition ${
                                isUnread ? 'bg-purple-500/5' : ''
                            }`}
                        >
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                                isUnread ? 'bg-gradient-to-br from-purple-500 to-orange-500 text-white' : 'bg-white/5 text-white/40'
                            }`}>
                                <Icon className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-white leading-snug">{n.message || n.type}</div>
                                <div className="text-xs text-white/40 mt-0.5">{timeAgo(n.created_at)}</div>
                            </div>
                            {isUnread && (
                                <div className="flex-shrink-0 w-2 h-2 rounded-full bg-purple-400 mt-2" />
                            )}
                        </button>
                    );
                })}

                <div className="px-4 py-2 border-t border-white/10">
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setOpen(false); navigate(`${basePath}/reseau/notifications`); }}
                        className="w-full text-xs text-white/60 hover:text-white"
                        data-testid="see-all-notifs-btn"
                    >
                        Voir toutes les notifications réseau
                    </Button>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
